import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { checkIsAdminLoggined } from '../../../../utils/utils.js';
import { paymentsApi, bankAccountsApi, paymentOrdersApi } from '../../../../api/bankApi';
import { Helmet } from 'react-helmet';
import ReactPaginate from 'react-paginate';
import Footer from '../../../Footer';
import Header from '../../../Header';
import PaymentAttachmentModal from '../payment/PaymentAttachmentModal';
import privatbankIcon from '../../../../assets/images/privatbank.png';
import monobankIcon from '../../../../assets/images/monobank.png';
import AdminPaymentsPageMobile from './AdminPaymentsPageMobile';

const AdminPaymentsPage = props => {
  const token = localStorage.getItem('token');
  const history = useHistory();
  const location = useLocation();

  const urlParams = queryString.parse(location.search);

  const [payments, setPayments] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentAttachmentsMap, setPaymentAttachmentsMap] = useState({}); // Map paymentId -> array of orderIds
  const [currentPage, setCurrentPage] = useState(() => {
    const page = urlParams.page ? parseInt(urlParams.page) : 1;
    return page - 1;
  });
  const [numberPerPage, setNumberPerPage] = useState(50);
  const [totalNumber, setTotalNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [selectedPaymentAttachments, setSelectedPaymentAttachments] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [selectedPaymentForAttach, setSelectedPaymentForAttach] = useState(null);
  const [attachAnchor, setAttachAnchor] = useState(null);
  const [attachSaving, setAttachSaving] = useState(false);
  const [showArchiveDropdown, setShowArchiveDropdown] = useState({}); // Map paymentId -> boolean
  const [selectedPaymentForArchive, setSelectedPaymentForArchive] = useState(null);
  const [showUnarchiveDropdown, setShowUnarchiveDropdown] = useState({}); // Map paymentId -> boolean
  const [selectedPaymentForUnarchive, setSelectedPaymentForUnarchive] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCommentDropdown, setShowCommentDropdown] = useState({}); // Map paymentId -> boolean
  const [selectedPaymentForComment, setSelectedPaymentForComment] = useState(null);
  /** Ключ `${paymentId}-comment|archive|unarchive` → відкрити поповер над кнопкою чи під нею */
  const [actionPopoverPlacement, setActionPopoverPlacement] = useState({});
  const [commentText, setCommentText] = useState(''); // Текст коментаря для редагування
  const [showFullCommentModal, setShowFullCommentModal] = useState(false);
  const [fullCommentText, setFullCommentText] = useState('');
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentError, setManualPaymentError] = useState('');
  const [manualPaymentForm, setManualPaymentForm] = useState({
    amount: '',
    type: 'IN',
    adminComment: ''
  });
  const [showEditManualPaymentModal, setShowEditManualPaymentModal] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [editManualPaymentForm, setEditManualPaymentForm] = useState({ amount: '', type: 'IN', adminComment: '' });
  const [editManualPaymentError, setEditManualPaymentError] = useState('');

  const hasUrlParams = Object.keys(urlParams).length > 0;

  // Filters - initialize from URL only, no defaults
  const [paymentIdFilter, setPaymentIdFilter] = useState(urlParams.paymentId || '');
  const [bankAccountIdFilter, setBankAccountIdFilter] = useState(urlParams.bankAccountId || '');
  const [bankTypeFilter, setBankTypeFilter] = useState(urlParams.bankType || '');
  const [typeFilter, setTypeFilter] = useState(urlParams.type || '');
  const [dateFromFilter, setDateFromFilter] = useState(urlParams.dateFrom?.split('T')[0] || '');
  const [dateToFilter, setDateToFilter] = useState(urlParams.dateTo?.split('T')[0] || '');
  const [amountFilter, setAmountFilter] = useState(urlParams.amount || '');
  // Status filter can be array or single value from URL
  const getStatusFilterFromUrl = () => {
    if (!urlParams.status) return [];
    if (Array.isArray(urlParams.status)) {
      return urlParams.status;
    }
    return [urlParams.status];
  };
  const [statusFilter, setStatusFilter] = useState(getStatusFilterFromUrl());
  const [searchQuery, setSearchQuery] = useState(urlParams.searchQuery || '');

  useEffect(() => {
    checkIsAdminLoggined(props);
    window.scrollTo(0, 0);
    loadBankAccounts();
  }, []);

  // Initialize filters from URL
  const [isInitializingFromURL, setIsInitializingFromURL] = useState(true);
  // Store initial page from URL to prevent it from being reset
  const initialPageFromURL = React.useRef((() => {
    const page = urlParams.page ? parseInt(urlParams.page) : 1;
    return page - 1;
  })());
  // Track if we had URL params on initial load
  const hadUrlParamsOnMount = React.useRef(hasUrlParams);

  // Load payments after filters are initialized from URL (only once on mount)
  useEffect(() => {
    if (isInitializingFromURL) {
      // Use the initial page from URL, not the current state
      loadPayments(initialPageFromURL.current);
      // Update URL after initialization to ensure it matches the current state
      // Use setTimeout to ensure this happens after all state updates
      setTimeout(() => {
        updateURL(initialPageFromURL.current);
        setIsInitializingFromURL(false);
      }, 150);
    }
  }, [isInitializingFromURL]);

  // Reset page to 0 when filters change (but not when currentPage or numberPerPage changes, and not during initialization from URL)
  useEffect(() => {
    // Don't reset currentPage during initialization from URL or if we had URL params on mount
    // Only reset if user manually changes filters (not during initial load)
    if (!isInitializingFromURL && !hadUrlParamsOnMount.current) {
      setCurrentPage(0);
    }
  }, [paymentIdFilter, bankAccountIdFilter, bankTypeFilter, typeFilter, dateFromFilter, dateToFilter, amountFilter, statusFilter, searchQuery, isInitializingFromURL]);

  // Load payments and update URL when filters or pagination changes (but not during initial load from URL)
  useEffect(() => {
    if (!isInitializingFromURL) {
      // If we had URL params on mount and this is the first time isInitializingFromURL becomes false,
      // don't update URL yet - it was already updated during initialization
      if (hadUrlParamsOnMount.current) {
        // Skip the first update after initialization, but allow subsequent updates
        hadUrlParamsOnMount.current = false;
        return;
      }
      updateURL(currentPage);
      loadPayments(currentPage);
    }
  }, [currentPage, numberPerPage, paymentIdFilter, bankAccountIdFilter, bankTypeFilter, typeFilter, dateFromFilter, dateToFilter, amountFilter, statusFilter, searchQuery, isInitializingFromURL]);

  // Відстежуємо зміни location.search і оновлюємо фільтри з URL (для навігації та очищення)
  useEffect(() => {
    if (!isInitializingFromURL) {
      const params = queryString.parse(location.search);
      
      // Отримуємо значення з URL (без дефолтних значень)
      const newPaymentId = params.paymentId || '';
      const newBankAccountId = params.bankAccountId || '';
      const newBankType = params.bankType || '';
      const newType = params.type || '';
      const newDateFrom = params.dateFrom?.split('T')[0] || '';
      const newDateTo = params.dateTo?.split('T')[0] || '';
      const newAmount = params.amount || '';
      // Status can be array or single value from URL
      const getStatusFromParams = () => {
        if (!params.status) return [];
        if (Array.isArray(params.status)) {
          return params.status;
        }
        return [params.status];
      };
      const newStatus = getStatusFromParams();
      const newSearchQuery = params.searchQuery || '';
      const newPage = params.page ? parseInt(params.page) - 1 : 0;
      
      // Перевіряємо, чи URL відрізняється від поточних фільтрів
      const statusArraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        const sorted1 = [...arr1].sort();
        const sorted2 = [...arr2].sort();
        return sorted1.every((val, idx) => val === sorted2[idx]);
      };
      
      const urlMatchesState = 
        newPaymentId === paymentIdFilter &&
        newBankAccountId === bankAccountIdFilter &&
        newBankType === bankTypeFilter &&
        newType === typeFilter &&
        newDateFrom === dateFromFilter &&
        newDateTo === dateToFilter &&
        newAmount === amountFilter &&
        statusArraysEqual(newStatus, statusFilter) &&
        newSearchQuery === searchQuery &&
        newPage === currentPage;
      
      // Якщо URL відповідає поточним фільтрам, не робимо нічого (щоб уникнути циклу)
      if (!urlMatchesState) {
        // Оновлюємо фільтри з URL (тільки якщо вони відрізняються)
        if (newPaymentId !== paymentIdFilter) {
          setPaymentIdFilter(newPaymentId);
        }
        if (newBankAccountId !== bankAccountIdFilter) {
          setBankAccountIdFilter(newBankAccountId);
        }
        if (newBankType !== bankTypeFilter) {
          setBankTypeFilter(newBankType);
        }
        if (newType !== typeFilter) {
          setTypeFilter(newType);
        }
        if (newDateFrom !== dateFromFilter) {
          setDateFromFilter(newDateFrom);
        }
        if (newDateTo !== dateToFilter) {
          setDateToFilter(newDateTo);
        }
        if (newAmount !== amountFilter) {
          setAmountFilter(newAmount);
        }
        if (!statusArraysEqual(newStatus, statusFilter)) {
          setStatusFilter(newStatus);
        }
        if (newSearchQuery !== searchQuery) {
          setSearchQuery(newSearchQuery);
        }
        if (newPage !== currentPage) {
          setCurrentPage(newPage);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Закриваємо випадаючі списки при кліку поза ними
  useEffect(() => {
    const handleClickOutside = (event) => {
      const hasOpenArchiveDropdown = Object.values(showArchiveDropdown).some(v => v === true);
      const hasOpenUnarchiveDropdown = Object.values(showUnarchiveDropdown).some(v => v === true);
      const hasOpenCommentDropdown = Object.values(showCommentDropdown).some(v => v === true);
      
      if (showStatusDropdown) {
        const statusDropdownContainer = event.target.closest('.status-dropdown-container');
        if (!statusDropdownContainer) {
          setShowStatusDropdown(false);
        }
      }
      
      if (hasOpenArchiveDropdown) {
        const dropdownContainer = event.target.closest('.payment-archive-dropdown-container');
        if (!dropdownContainer) {
          setShowArchiveDropdown({});
          setSelectedPaymentForArchive(null);
        }
      }
      
      if (hasOpenUnarchiveDropdown) {
        const dropdownContainer = event.target.closest('.payment-unarchive-dropdown-container');
        if (!dropdownContainer) {
          setShowUnarchiveDropdown({});
          setSelectedPaymentForUnarchive(null);
        }
      }
      
      if (hasOpenCommentDropdown) {
        const dropdownContainer = event.target.closest('.payment-comment-dropdown-container');
        const isInsideDropdown = event.target.closest('.payment-action-popover');
        
        if (!dropdownContainer && !isInsideDropdown) {
          setShowCommentDropdown({});
          setSelectedPaymentForComment(null);
          setCommentText('');
        }
      }
    };

    if (showStatusDropdown || Object.values(showArchiveDropdown).some(v => v === true) || Object.values(showUnarchiveDropdown).some(v => v === true) || Object.values(showCommentDropdown).some(v => v === true)) {
      // Використовуємо click замість mousedown, щоб не блокувати інпут
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showStatusDropdown, showArchiveDropdown, showUnarchiveDropdown, showCommentDropdown]);

  const loadBankAccounts = () => {
    bankAccountsApi.getAll()
      .then(res => {
        if (res.status === 200 && res.data.data) {
          setBankAccounts(res.data.data);
        }
      })
      .catch(error => {
        console.error('Error loading bank accounts:', error);
      });
  };

  const loadPayments = async (page) => {
    setIsLoading(true);
    const params = {
      page: (page || currentPage) + 1,
      numberPerPage,
    };

    if (paymentIdFilter) params.paymentId = paymentIdFilter;
    if (bankAccountIdFilter) params.bankAccountId = bankAccountIdFilter;
    if (bankTypeFilter) params.bankType = bankTypeFilter;
    if (typeFilter) params.type = typeFilter;
    if (dateFromFilter) params.dateFrom = dateFromFilter + 'T00:00:00';
    if (dateToFilter) params.dateTo = dateToFilter + 'T23:59:59';
    if (amountFilter) params.amount = amountFilter;
    if (statusFilter && statusFilter.length > 0) params.status = statusFilter;
    if (searchQuery) params.searchQuery = searchQuery;

    try {
      const res = await paymentsApi.getAll(params);
      if (res.status === 200 && res.data.data) {
        setPayments(res.data.data);
        setTotalNumber(res.data.totalNumber || 0);
        setPageCount(Math.ceil((res.data.totalNumber || 0) / numberPerPage));
        
        // Оновлюємо paymentAttachmentsMap з даних, отриманих з API
        const newPaymentAttachmentsMap = {};
        res.data.data.forEach(payment => {
          if (payment.attachedOrderIds && payment.attachedOrderIds.length > 0) {
            newPaymentAttachmentsMap[payment.id] = payment.attachedOrderIds;
          }
        });
        setPaymentAttachmentsMap(newPaymentAttachmentsMap);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAttachedPayment = async paymentId => {
    const response = await paymentsApi.getById(paymentId);
    const updated = response.data.data;
    if (!updated || Number(updated.id) !== Number(paymentId)) {
      throw new Error('Не вдалося оновити оплату');
    }
    setPayments(previous => previous.map(payment => payment.id === updated.id ? { ...payment, ...updated } : payment));
    setPaymentAttachmentsMap(previous => ({ ...previous, [updated.id]: updated.attachedOrderIds || [] }));
  };

  const updateURL = (page = currentPage) => {
    const params = {};
    if (paymentIdFilter) params.paymentId = paymentIdFilter;
    if (bankAccountIdFilter) params.bankAccountId = bankAccountIdFilter;
    if (bankTypeFilter) params.bankType = bankTypeFilter;
    if (typeFilter) params.type = typeFilter;
    if (dateFromFilter) params.dateFrom = dateFromFilter;
    if (dateToFilter) params.dateTo = dateToFilter;
    if (amountFilter) params.amount = amountFilter;
    if (statusFilter && statusFilter.length > 0) params.status = statusFilter;
    if (searchQuery) params.searchQuery = searchQuery;
    if (page >= 0) params.page = page + 1;

    const query = queryString.stringify(params);
    const newUrl = `/admin/payments${query ? `?${query}` : ''}`;
    if (location.pathname + location.search !== newUrl) {
      history.push(newUrl);
    }
  };

  const handlePageClick = (data) => {
    const page = data.selected;
    setCurrentPage(page);
    updateURL(page);
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    updateURL(0);
    loadPayments(0);
  };

  const handleResetFilters = () => {
    setPaymentIdFilter('');
    setBankAccountIdFilter('');
    setBankTypeFilter('');
    setTypeFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setAmountFilter('');
    setStatusFilter([]);
    setSearchQuery('');
    setCurrentPage(0);
    // Встановлюємо hadUrlParamsOnMount в false, щоб useEffect на фільтрах спрацював
    hadUrlParamsOnMount.current = false;
    setIsInitializingFromURL(false);
    // Очищаємо URL
    history.push('/admin/payments');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const currentYear = new Date().getFullYear();
    const year = date.getFullYear();

    if (year === currentYear) {
      // Якщо поточний рік - не показуємо рік
      return date.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } else {
      // Якщо минулий рік - показуємо рік
      return date.toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  /** Один формат дати оплати: без секунд; рік тільки якщо не поточний. Приймає рядок з бекенду "dd.MM.yyyy, HH:mm:ss" або ISO. */
  const formatPaymentDateDisplay = (value) => {
    if (!value) return '-';
    const str = typeof value === 'string' ? value : (value && value.toISOString ? value.toISOString() : String(value));
    const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4}),\s*(\d{2}):(\d{2})/);
    if (match) {
      const [, d, m, y, hour, min] = match;
      const year = parseInt(y, 10);
      const currentYear = new Date().getFullYear();
      return year === currentYear ? `${d}.${m}, ${hour}:${min}` : `${d}.${m}.${y}, ${hour}:${min}`;
    }
    const date = new Date(str);
    if (Number.isNaN(date.getTime())) return str;
    const currentYear = new Date().getFullYear();
    const year = date.getFullYear();
    return year === currentYear
      ? date.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPaymentDate = (createdAt, transactionDate) => {
    const value = createdAt || transactionDate;
    if (!value) return '-';
    return formatPaymentDateDisplay(value);
  };

  const formatCurrency = (amount, showCurrency = true) => {
    if (!amount) return showCurrency ? '0 грн' : '0';
    const formatted = parseFloat(amount).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return showCurrency ? `${formatted} грн` : formatted;
  };

  const computeActionPopoverPlacement = (triggerEl, estimatedHeightPx = 300) => {
    if (!triggerEl || typeof triggerEl.getBoundingClientRect !== 'function') return 'below';
    const rect = triggerEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const margin = 12;
    const need = estimatedHeightPx + margin;
    if (spaceBelow >= need) return 'below';
    // Не відкривати вгору, якщо зверху лише трохи більше місця за низ (інакше «Нова» з великою оцінкою хибно перевертала б панель)
    const flipAdvantagePx = 96;
    if (spaceAbove > spaceBelow + flipAdvantagePx) return 'above';
    return 'below';
  };

  const setActionPopoverPlacementForPayment = (paymentId, kind, event, estimatedHeightPx) => {
    const placement = computeActionPopoverPlacement(event?.currentTarget, estimatedHeightPx);
    const key = `${paymentId}-${kind}`;
    setActionPopoverPlacement(prev => ({ ...prev, [key]: placement }));
  };

  const handleOpenArchiveDropdown = (payment, event) => {
    setSelectedPaymentForArchive(payment);
    setActionPopoverPlacementForPayment(payment.id, 'archive', event, 220);
    setShowArchiveDropdown(prev => ({ ...prev, [payment.id]: true }));
  };

  const handleArchivePayment = async () => {
    if (!selectedPaymentForArchive) {
      return;
    }

    try {
      const res = await paymentsApi.updateStatus(selectedPaymentForArchive.id, 'ARCHIVED');
      if (res.status === 200) {
        // Закриваємо випадаючий список
        setShowArchiveDropdown(prev => ({ ...prev, [selectedPaymentForArchive.id]: false }));
        // Оновлюємо статус оплати в локальному стані без перезавантаження
        setPayments(prevPayments =>
          prevPayments.map(payment =>
            payment.id === selectedPaymentForArchive.id
              ? {
                  ...payment,
                  status: {
                    ...payment.status,
                    databaseValue: 'ARCHIVED',
                    displayName: 'Архівована',
                    adminValue: 'Архівована',
                  },
                }
              : payment,
          ),
        );
        setSelectedPaymentForArchive(null);
      }
    } catch (error) {
      console.error('Error archiving payment:', error);
      alert(error.response?.data?.data || 'Помилка архівування оплати');
    }
  };

  const handleOpenUnarchiveDropdown = (payment, event) => {
    setSelectedPaymentForUnarchive(payment);
    setActionPopoverPlacementForPayment(payment.id, 'unarchive', event, 220);
    setShowUnarchiveDropdown(prev => ({ ...prev, [payment.id]: true }));
  };

  const handleUnarchivePayment = async () => {
    if (!selectedPaymentForUnarchive) {
      return;
    }

    try {
      const res = await paymentsApi.updateStatus(selectedPaymentForUnarchive.id, 'PENDING');
      if (res.status === 200) {
        // Закриваємо випадаючий список
        setShowUnarchiveDropdown(prev => ({ ...prev, [selectedPaymentForUnarchive.id]: false }));
        // Оновлюємо статус оплати в локальному стані без перезавантаження
        setPayments(prevPayments =>
          prevPayments.map(payment =>
            payment.id === selectedPaymentForUnarchive.id
              ? {
                  ...payment,
                  status: {
                    ...payment.status,
                    databaseValue: 'PENDING',
                    displayName: 'Нова',
                    adminValue: 'Нова',
                  },
                }
              : payment,
          ),
        );
        setSelectedPaymentForUnarchive(null);
      }
    } catch (error) {
      console.error('Error unarchiving payment:', error);
      alert(error.response?.data?.data || 'Помилка повернення оплати з архіву');
    }
  };

  const handleOpenCommentDropdown = (payment, event) => {
    setSelectedPaymentForComment(payment);
    setCommentText(payment.adminComment || '');
    setActionPopoverPlacementForPayment(payment.id, 'comment', event, 280);
    setShowCommentDropdown(prev => ({ ...prev, [payment.id]: true }));
  };

  const handleSaveComment = async () => {
    if (!selectedPaymentForComment) {
      return;
    }

    try {
      await paymentsApi.updateComment(selectedPaymentForComment.id, commentText);
      
      // Оновлюємо оплату в локальному стані
      setPayments(prevPayments =>
        prevPayments.map(p =>
          p.id === selectedPaymentForComment.id
            ? { ...p, adminComment: commentText }
            : p
        )
      );
      
      // Закриваємо випадаючий список
      setShowCommentDropdown(prev => ({ ...prev, [selectedPaymentForComment.id]: false }));
      setSelectedPaymentForComment(null);
      setCommentText('');
    } catch (error) {
      console.error('Error updating comment:', error);
      alert(error.response?.data?.data || 'Помилка оновлення коментаря');
    }
  };

  const handleCreateManualPayment = async () => {
    setManualPaymentError('');
    // Валідація
    if (!manualPaymentForm.amount || parseFloat(manualPaymentForm.amount) <= 0) {
      setManualPaymentError('Сума оплати повинна бути більше 0');
      return;
    }

    try {
      const requestData = {
        amount: parseFloat(manualPaymentForm.amount),
        type: manualPaymentForm.type,
        // Для ручних оплат не передаємо transactionDate - воно буде NULL
        adminComment: manualPaymentForm.adminComment || null
      };

      await paymentsApi.createManual(requestData);
      
      // Закриваємо модальне вікно та очищаємо форму
      setShowManualPaymentModal(false);
      setManualPaymentError('');
      setManualPaymentForm({
        amount: '',
        type: 'IN',
        adminComment: ''
      });

      // Оновлюємо список оплат
      loadPayments(currentPage);
    } catch (error) {
      console.error('Error creating manual payment:', error);
      setManualPaymentError(error.response?.data?.data || 'Помилка створення оплати');
    }
  };

  const handleOpenEditManualPayment = (payment) => {
    const typeVal = payment.type?.databaseValue || payment.type?.name || payment.type || 'IN';
    setPaymentToEdit(payment);
    setEditManualPaymentForm({
      amount: String(payment.amount ?? ''),
      type: typeVal === 'OUT' || typeVal === 'OUTGOING' ? 'OUT' : 'IN',
      adminComment: payment.adminComment || ''
    });
    setEditManualPaymentError('');
    setShowEditManualPaymentModal(true);
  };

  const handleUpdateManualPayment = async () => {
    setEditManualPaymentError('');
    if (!paymentToEdit) return;
    const amount = parseFloat(editManualPaymentForm.amount);
    if (!editManualPaymentForm.amount || isNaN(amount) || amount <= 0) {
      setEditManualPaymentError('Сума оплати повинна бути більше 0');
      return;
    }
    try {
      await paymentsApi.updateManual(paymentToEdit.id, {
        amount,
        type: editManualPaymentForm.type,
        adminComment: editManualPaymentForm.adminComment || null
      });
      setShowEditManualPaymentModal(false);
      setPaymentToEdit(null);
      loadPayments(currentPage);
    } catch (error) {
      console.error('Error updating manual payment:', error);
      setEditManualPaymentError(error.response?.data?.data || 'Помилка оновлення оплати');
    }
  };

  return (
    <>
      <PaymentAttachmentModal payment={selectedPaymentForAttach}
        presentation='popover' anchor={attachAnchor} onBusyChange={setAttachSaving}
        onClose={() => { setSelectedPaymentForAttach(null); setAttachAnchor(null); setAttachSaving(false); }}
        onAttached={refreshAttachedPayment} />
      <div className="d-lg-none">
        <AdminPaymentsPageMobile {...props} />
      </div>
      <div className="d-none d-lg-block">
    <div className='container'>
      <Helmet>
        <title>Адмін панель - Оплати</title>
      </Helmet>
      <div className='row'>
        <div className='col p-0'>
          <Header />
        </div>
      </div>

      {/* Filters - First Row */}
      <div className='row filters mb-3'>
        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Рахунок:</p>
          <select
            className='form-control'
            value={bankAccountIdFilter}
            onChange={(e) => setBankAccountIdFilter(e.target.value)}
            style={{ fontSize: '0.875rem' }}
          >
            <option value=''>Всі рахунки</option>
            {bankAccounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.name} ({account.bankType === 'MONOBANK' ? 'Моно' : 'Приват'})
              </option>
            ))}
          </select>
        </div>

        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Тип:</p>
          <select
            className='form-control'
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ fontSize: '0.875rem' }}
          >
            <option value=''>Всі</option>
            <option value='IN'>Вхідні</option>
            <option value='OUT'>Вихідні</option>
          </select>
        </div>

        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Статус:</p>
          <div className='position-relative status-dropdown-container'>
            <button
              type='button'
              className='form-control text-left d-flex justify-content-between align-items-center'
              onClick={(e) => {
                e.stopPropagation();
                setShowStatusDropdown(!showStatusDropdown);
              }}
              style={{ fontSize: '0.875rem', backgroundColor: 'white', cursor: 'pointer' }}
            >
              <span>
                {statusFilter.length === 0
                  ? 'Всі'
                  : statusFilter.length === 1
                  ? (() => {
                      const statusLabels = {
                        'PENDING': 'Нова',
                        'PARTIALLY_USED': 'Частково використана',
                        'USED': 'Використана',
                        'ARCHIVED': 'Архівована'
                      };
                      return statusLabels[statusFilter[0]] || statusFilter[0];
                    })()
                  : `Обрано: ${statusFilter.length}`}
              </span>
              <i className={`bi bi-chevron-${showStatusDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showStatusDropdown && (
              <div
                className='position-absolute w-100 border rounded bg-white shadow-sm'
                style={{ zIndex: 1000, marginTop: '2px', maxHeight: '200px', overflowY: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className='p-2'>
                  <div className='form-check d-flex align-items-center'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='status-pending'
                      checked={statusFilter.includes('PENDING')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStatusFilter([...statusFilter, 'PENDING']);
                        } else {
                          setStatusFilter(statusFilter.filter(s => s !== 'PENDING'));
                        }
                      }}
                      style={{ width: '18px', height: '18px', marginTop: '0', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label className='form-check-label ml-2' htmlFor='status-pending' style={{ fontSize: '0.875rem', cursor: 'pointer', marginBottom: '0', flex: 1 }}>
                      Нова
                    </label>
                  </div>
                  <div className='form-check d-flex align-items-center'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='status-partially-used'
                      checked={statusFilter.includes('PARTIALLY_USED')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStatusFilter([...statusFilter, 'PARTIALLY_USED']);
                        } else {
                          setStatusFilter(statusFilter.filter(s => s !== 'PARTIALLY_USED'));
                        }
                      }}
                      style={{ width: '18px', height: '18px', marginTop: '0', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label className='form-check-label ml-2' htmlFor='status-partially-used' style={{ fontSize: '0.875rem', cursor: 'pointer', marginBottom: '0', flex: 1 }}>
                      Частково використана
                    </label>
                  </div>
                  <div className='form-check d-flex align-items-center'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='status-used'
                      checked={statusFilter.includes('USED')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStatusFilter([...statusFilter, 'USED']);
                        } else {
                          setStatusFilter(statusFilter.filter(s => s !== 'USED'));
                        }
                      }}
                      style={{ width: '18px', height: '18px', marginTop: '0', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label className='form-check-label ml-2' htmlFor='status-used' style={{ fontSize: '0.875rem', cursor: 'pointer', marginBottom: '0', flex: 1 }}>
                      Використана
                    </label>
                  </div>
                  <div className='form-check d-flex align-items-center'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='status-archived'
                      checked={statusFilter.includes('ARCHIVED')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setStatusFilter([...statusFilter, 'ARCHIVED']);
                        } else {
                          setStatusFilter(statusFilter.filter(s => s !== 'ARCHIVED'));
                        }
                      }}
                      style={{ width: '18px', height: '18px', marginTop: '0', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label className='form-check-label ml-2' htmlFor='status-archived' style={{ fontSize: '0.875rem', cursor: 'pointer', marginBottom: '0', flex: 1 }}>
                      Архівована
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Сума:</p>
          <input
            type='number'
            className='form-control'
            step='0.01'
            min='0'
            value={amountFilter}
            onChange={(e) => setAmountFilter(e.target.value)}
            placeholder='Точна сума'
            style={{ fontSize: '0.875rem' }}
          />
        </div>

        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Дата від:</p>
          <input
            type='date'
            className='form-control'
            value={dateFromFilter}
            onChange={(e) => setDateFromFilter(e.target.value)}
            style={{ fontSize: '0.875rem' }}
          />
        </div>

        <div className='col-2 py-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Дата до:</p>
          <input
            type='date'
            className='form-control'
            value={dateToFilter}
            onChange={(e) => setDateToFilter(e.target.value)}
            style={{ fontSize: '0.875rem' }}
          />
        </div>
      </div>

      {/* Filters - Second Row (Search) */}
      <div className='row filters mb-3'>
        <div className='col-2 pb-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>ID оплати:</p>
          <input
            type='number'
            className='form-control'
            min='1'
            value={paymentIdFilter}
            onChange={(e) => setPaymentIdFilter(e.target.value)}
            placeholder='ID оплати'
            style={{ fontSize: '0.875rem' }}
          />
        </div>
        <div className='col-10 pb-1 px-1'>
          <p className='mb-1 font-weight-bold' style={{ fontSize: '0.875rem' }}>Пошук (опис, контрагент):</p>
          <div className='position-relative d-flex align-items-center'>
            <input
              type='text'
              className='form-control pr-4'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Введіть текст для пошуку'
              style={{ fontSize: '0.875rem' }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            {searchQuery && (
              <button
                type='button'
                className='btn p-0 border-0 bg-transparent position-absolute'
                style={{ right: '8px', fontSize: '1.1rem', lineHeight: 1, color: '#6c757d', cursor: 'pointer' }}
                onClick={() => setSearchQuery('')}
                title='Очистити пошук'
                aria-label='Очистити пошук'
              >
                <i className='bi bi-x'></i>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-12 p-0'>
          <div className='mb-3 d-flex justify-content-between align-items-start flex-wrap' style={{ gap: '0.5rem' }}>
            {/* Active Filters */}
            <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.5rem', flex: '1', minWidth: '0' }}>
              {(paymentIdFilter || bankAccountIdFilter || typeFilter || (statusFilter && statusFilter.length > 0) || amountFilter || dateFromFilter || dateToFilter || searchQuery) ? (
                <>
                  <span className='font-weight-bold' style={{ fontSize: '0.875rem' }}>Активні фільтри:</span>
                {paymentIdFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    ID: {paymentIdFilter}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setPaymentIdFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {bankAccountIdFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Рахунок: {bankAccounts.find(a => a.id === parseInt(bankAccountIdFilter))?.name || bankAccountIdFilter}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setBankAccountIdFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {typeFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Тип: {typeFilter === 'IN' ? 'Вхідні' : 'Вихідні'}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setTypeFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {statusFilter && statusFilter.length > 0 && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Статус: {(() => {
                      const statusLabels = {
                        'PENDING': 'Нова',
                        'PARTIALLY_USED': 'Частково використана',
                        'USED': 'Використана',
                        'ARCHIVED': 'Архівована'
                      };
                      return statusFilter.map(status => statusLabels[status] || status).join(', ');
                    })()}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setStatusFilter([])}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {amountFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Сума: {amountFilter} грн
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setAmountFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {dateFromFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Дата від: {dateFromFilter}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setDateFromFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {dateToFilter && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Дата до: {dateToFilter}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setDateToFilter('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className='badge badge-dark d-inline-flex align-items-center' style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                    Пошук: {searchQuery}
                    <button
                      type='button'
                      className='btn p-0 ml-2 border-0 bg-transparent'
                      onClick={() => setSearchQuery('')}
                      title='Скинути фільтр'
                      style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      <i className='bi bi-x' style={{ fontWeight: 'bold' }}></i>
                    </button>
                  </span>
                  )}
                </>
              ) : null}
            </div>
            <div className='d-flex align-items-center' style={{ flexShrink: 0 }}>
              <button className='btn btn-danger mr-2' onClick={handleResetFilters} style={{ fontSize: '0.875rem' }}>
                Очистити фільтр
              </button>
              <button className='btn btn-dark mr-2' onClick={handleSearch} style={{ fontSize: '0.875rem' }}>
                Оновити
              </button>
              <button 
                className='btn btn-success' 
                onClick={() => { setShowManualPaymentModal(true); setManualPaymentError(''); }} 
                style={{ fontSize: '0.875rem' }}
              >
                <i className='bi bi-plus-circle mr-1'></i>
                Додати оплату вручну
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-12 p-0'>
          <div className='text-center mb-3'>
            <h5 className='text-center font-weight-bold mb-0'>Всього оплат: {totalNumber}</h5>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col p-0'>
          {isLoading ? (
            <div className='text-center py-5'>
              <div className='spinner-border' role='status'>
                <span className='sr-only'>Завантаження...</span>
              </div>
            </div>
          ) : payments.length === 0 ? (
            <div className='text-center py-5'>
              <p>Оплат не знайдено</p>
            </div>
          ) : (
            <>
              <div className='table-responsive'>
                <table className='table table-striped table-hover' style={{ fontSize: '0.875rem' }}>
                  <thead>
                  <tr>
                    <th style={{ width: '110px' }}>Дата</th>
                    <th style={{ width: '3px', padding: 0 }}></th>
                    <th>Деталі платежу</th>
                    <th style={{ width: '150px' }}>Сума</th>
                    <th style={{ width: '120px' }}>Статус</th>
                    <th style={{ width: '80px' }}>Дії</th>
                  </tr>
                  </thead>
                  <tbody>
                  {payments.map(payment => {
                    const paymentType = payment.type?.databaseValue || payment.type?.name || payment.type;
                    const isIn = paymentType === 'IN' || paymentType === 'INCOMING';
                    const borderColor = isIn ? '#28a745' : '#dc3545';

                    return (
                      <tr key={payment.id}>
                        <td style={{ fontSize: '0.875rem', width: '110px' }}>{formatPaymentDate(payment.transactionDate, payment.createdAt)}</td>
                        <td style={{
                          width: '3px',
                          padding: 0,
                          backgroundColor: borderColor,
                          minWidth: '3px',
                        }}></td>
                        <td style={{ fontSize: '0.875rem' }}>
                          <div className='font-weight-bold'>
                            <span className="text-muted mr-2">#{payment.id}</span>
                            {!payment.isManual && payment.bankType?.databaseValue === 'PRIVATBANK' && (
                              <img 
                                src={privatbankIcon} 
                                alt='PrivatBank' 
                                style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                              />
                            )}
                            {!payment.isManual && payment.bankType?.databaseValue === 'MONOBANK' && (
                              <img 
                                src={monobankIcon} 
                                alt='Monobank' 
                                style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                              />
                            )}
                            {payment.isManual ? 'Ручна оплата' : (payment.bankAccountName || payment.bankAccountId)}
                            {paymentAttachmentsMap[payment.id] && paymentAttachmentsMap[payment.id].length > 0 && (
                              <>
                                {paymentAttachmentsMap[payment.id].map((orderId, index) => (
                                  <a
                                    key={orderId}
                                    href={`/admin/order/${orderId}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='ml-2'
                                    style={{ color: '#007bff', textDecoration: 'none' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <i className='bi bi-cart' style={{ fontSize: '0.875rem' }}></i>
                                    {orderId}
                                  </a>
                                ))}
                              </>
                            )}
                          </div>
                          <div>
                            {payment.bankDescription && <div>{payment.bankDescription}</div>}
                            {payment.counterparty && <div>{payment.counterparty}</div>}
                            {payment.adminComment && (
                              <div 
                                className='text-muted mt-1' 
                                style={{ 
                                  fontSize: '0.875rem', 
                                  fontStyle: 'italic',
                                  cursor: 'pointer',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }}
                                onClick={() => {
                                  setFullCommentText(payment.adminComment);
                                  setShowFullCommentModal(true);
                                }}
                                title={payment.adminComment}
                              >
                                <i className='bi bi-chat-left-text mr-1'></i>
                                {payment.adminComment.length > 50 
                                  ? payment.adminComment.substring(0, 50) + '...' 
                                  : payment.adminComment}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={isIn ? 'text-success' : 'text-danger'}
                            style={{ fontSize: '1rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {payment.status?.databaseValue === 'PARTIALLY_USED' && payment.availableAmount ? (
                            <div>
                              <div>{(isIn ? '+' : '-') + formatCurrency(payment.amount)} грн</div>
                              <div style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                                <span className="text-warning">Використано: {formatCurrency(payment.amount - payment.availableAmount)}</span>
                                <span className="text-muted"> / </span>
                                <span className="text-success">Залишилось: {formatCurrency(payment.availableAmount)}</span>
                              </div>
                            </div>
                          ) : (
                            <div>{(isIn ? '+' : '-') + formatCurrency(payment.amount)} грн</div>
                          )}
                        </td>
                        <td style={{ fontSize: '0.9375rem' }}>
                            <span
                              className={`badge badge-${payment.status?.databaseValue === 'USED' ? 'success' : payment.status?.databaseValue === 'ARCHIVED' ? 'dark' : payment.status?.databaseValue === 'PARTIALLY_USED' ? 'warning' : 'secondary'}`}
                              style={{ fontSize: '0.9375rem', padding: '0.375rem 0.5rem' }}>
                              {payment.status?.displayName || payment.status || '-'}
                            </span>
                        </td>
                        <td>
                          <div className='d-flex gap-1'>
                            <button type="button" className="btn btn-link btn-sm"
                              id={`payment-attachment-trigger-${payment.id}`} disabled={attachSaving}
                              onClick={event => {
                                if (attachSaving) return;
                                if (selectedPaymentForAttach?.id === payment.id) { setSelectedPaymentForAttach(null); setAttachAnchor(null); }
                                else { setAttachAnchor(event.currentTarget); setSelectedPaymentForAttach(payment); }
                              }} title={payment.status?.databaseValue === 'USED' ? 'Прикріплені замовлення' : 'Прикріпити до замовлення'}
                              aria-label={payment.status?.databaseValue === 'USED' ? 'Прикріплені замовлення' : 'Прикріпити до замовлення'}
                              style={{ padding: '2px 4px', color: '#000' }}>
                              <i className="bi bi-link-45deg" style={{ fontSize: '1.125rem' }} />
                            </button>
                            {/* Іконка коментаря */}
                            <div className='position-relative payment-comment-dropdown-container' style={{ display: 'inline-block' }}>
                              <button
                                className='btn btn-link btn-sm'
                                onClick={(e) => handleOpenCommentDropdown(payment, e)}
                                title={payment.adminComment ? 'Редагувати коментар' : 'Додати коментар'}
                                style={{ 
                                  padding: '2px 4px', 
                                  color: payment.adminComment ? '#007bff' : '#000' 
                                }}
                              >
                                <i className='bi bi-chat-left-text' style={{ fontSize: '1.125rem' }}></i>
                              </button>
                              {showCommentDropdown[payment.id] && selectedPaymentForComment?.id === payment.id && (
                                <div 
                                  className='position-absolute bg-white border rounded shadow-lg p-3 payment-action-popover'
                                  style={{
                                    zIndex: 1050,
                                    width: '400px',
                                    right: 0,
                                    ...((actionPopoverPlacement[`${payment.id}-comment`] || 'below') === 'above'
                                      ? { bottom: '100%', marginBottom: '5px', top: 'auto', marginTop: 0 }
                                      : { top: '100%', marginTop: '5px', bottom: 'auto', marginBottom: 0 }),
                                  }}
                                >
                                  <label className='font-weight-bold mb-2 d-block'>Коментар:</label>
                                  <textarea
                                    className='form-control mb-3'
                                    rows='4'
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder='Введіть коментар (наприклад, від кого оплата)'
                                    style={{ fontSize: '0.875rem', resize: 'vertical' }}
                                  />
                                  <button
                                    type='button'
                                    className='btn btn-dark btn-sm btn-block'
                                    onClick={handleSaveComment}
                                  >
                                    Зберегти
                                  </button>
                                </div>
                              )}
                            </div>
                            {/* Редагувати ручну оплату */}
                            {payment.isManual && (
                              <button
                                type='button'
                                className='btn btn-link btn-sm'
                                onClick={() => handleOpenEditManualPayment(payment)}
                                title='Редагувати оплату'
                                style={{ padding: '2px 4px', color: '#000' }}
                              >
                                <i className='bi bi-pencil-fill text-dark' style={{ fontSize: '1.125rem' }}></i>
                              </button>
                            )}
                            {/* Показуємо кнопку архівувати для PENDING та частково використаних (PARTIALLY_USED) оплат */}
                            {(payment.status?.databaseValue === 'PENDING' || payment.status?.databaseValue === 'PARTIALLY_USED') && (
                              <div className='position-relative payment-archive-dropdown-container' style={{ display: 'inline-block' }}>
                                <button
                                  className='btn btn-link btn-sm'
                                  onClick={(e) => handleOpenArchiveDropdown(payment, e)}
                                  title='Архівувати транзакцію'
                                  style={{ padding: '2px 4px', color: '#000' }}
                                >
                                  <i className='bi bi-trash' style={{ fontSize: '1.125rem' }}></i>
                                </button>
                                {showArchiveDropdown[payment.id] && selectedPaymentForArchive?.id === payment.id && (
                                  <div 
                                    className='position-absolute bg-white border rounded shadow-lg p-3 payment-action-popover'
                                    style={{
                                      zIndex: 1050,
                                      width: '300px',
                                      right: 0,
                                      ...((actionPopoverPlacement[`${payment.id}-archive`] || 'below') === 'above'
                                        ? { bottom: '100%', marginBottom: '5px', top: 'auto', marginTop: 0 }
                                        : { top: '100%', marginTop: '5px', bottom: 'auto', marginBottom: 0 }),
                                    }}
                                  >
                                    <p className='mb-3'>Ви бажаєте архівувати цю транзакцію?</p>
                                    <button
                                      type='button'
                                      className='btn btn-dark btn-sm btn-block'
                                      onClick={handleArchivePayment}
                                    >
                                      Підтвердити
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Показуємо кнопку повернути з архіву тільки для ARCHIVED оплат */}
                            {payment.status?.databaseValue === 'ARCHIVED' && (
                              <div className='position-relative payment-unarchive-dropdown-container' style={{ display: 'inline-block' }}>
                                <button
                                  className='btn btn-link btn-sm'
                                  onClick={(e) => handleOpenUnarchiveDropdown(payment, e)}
                                  title='Повернути з архіву'
                                  style={{ padding: '2px 4px', color: '#000' }}
                                >
                                  <i className='bi bi-arrow-counterclockwise' style={{ fontSize: '1.125rem' }}></i>
                                </button>
                                {showUnarchiveDropdown[payment.id] && selectedPaymentForUnarchive?.id === payment.id && (
                                  <div 
                                    className='position-absolute bg-white border rounded shadow-lg p-3 payment-action-popover'
                                    style={{
                                      zIndex: 1050,
                                      width: '300px',
                                      right: 0,
                                      ...((actionPopoverPlacement[`${payment.id}-unarchive`] || 'below') === 'above'
                                        ? { bottom: '100%', marginBottom: '5px', top: 'auto', marginTop: 0 }
                                        : { top: '100%', marginTop: '5px', bottom: 'auto', marginBottom: 0 }),
                                    }}
                                  >
                                    <p className='mb-3'>Ви бажаєте повернути цю транзакцію з архіву?</p>
                                    <button
                                      type='button'
                                      className='btn btn-dark btn-sm btn-block'
                                      onClick={handleUnarchivePayment}
                                    >
                                      Підтвердити
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>

              {/* Модальне вікно для перегляду прикріплених замовлень */}
              <div 
                className='modal fade' 
                id='attachmentsModal' 
                tabIndex='-1' 
                role='dialog'
                aria-labelledby='attachmentsModalLabel'
                aria-hidden='true'
              >
                <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
                  <div className='modal-content'>
                    <div className='modal-header'>
                      <h5 className='modal-title font-weight-bold' id='attachmentsModalLabel'>Прикріплені замовлення
                        (Оплата #{selectedPaymentId})</h5>
                      <button 
                        type='button' 
                        className='close' 
                        data-dismiss='modal'
                        aria-label='Close'
                      >
                        <span aria-hidden='true'>&times;</span>
                      </button>
                    </div>
                    <div className='modal-body'>
                      {selectedPaymentAttachments.length > 0 ? (
                        <table className='table table-striped table-bordered table-hover table-sm'>
                          <thead>
                          <tr>
                            <th>ID замовлення</th>
                            <th>Сума замовлення</th>
                            <th>Прикріплена сума</th>
                            <th>Дата прикріплення</th>
                          </tr>
                          </thead>
                          <tbody>
                          {selectedPaymentAttachments.map((po) => (
                            <tr key={po.id}>
                              <td>
                                <a href={`/admin/order/${po.orderId}`} target='_blank' rel='noopener noreferrer'>
                                  {po.orderId}
                                </a>
                              </td>
                              <td>{formatCurrency(po.order?.discountedPrice || 0)}</td>
                              <td>{formatCurrency(po.amount)}</td>
                              <td>{formatDate(po.createdAt)}</td>
                            </tr>
                          ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className='text-muted'>Ця оплата не прикріплена до жодного замовлення</p>
                      )}
                    </div>
                    <div className='modal-footer'>
                      <button 
                        type='button' 
                        className='btn btn-secondary' 
                        data-dismiss='modal'
                      >
                        Закрити
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Модальне вікно для створення ручної оплати */}
              {showManualPaymentModal && (
                <div 
                  className='modal fade show' 
                  style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  tabIndex='-1' 
                  role='dialog'
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowManualPaymentModal(false);
                    }
                  }}
                >
                  <div className='modal-dialog modal-dialog-centered' role='document' onClick={(e) => e.stopPropagation()}>
                    <div className='modal-content'>
                      <div className='modal-header'>
                        <h5 className='modal-title font-weight-bold'>Додати оплату вручну</h5>
                        <button 
                          type='button' 
                          className='close' 
                          onClick={() => { setShowManualPaymentModal(false); setManualPaymentError(''); }}
                          aria-label='Close'
                        >
                          <span aria-hidden='true'>&times;</span>
                        </button>
                      </div>
                      <div className='modal-body'>
                        {manualPaymentError && (
                          <div className='alert alert-danger' role='alert'>
                            {manualPaymentError}
                          </div>
                        )}
                        <div className='form-group'>
                          <label className='font-weight-bold'>Сума *</label>
                          <input
                            type='number'
                            className='form-control'
                            step='0.01'
                            min='0.01'
                            value={manualPaymentForm.amount}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, amount: e.target.value })}
                            placeholder='Введіть суму'
                            required
                          />
                        </div>
                        <div className='form-group'>
                          <label className='font-weight-bold'>Тип *</label>
                          <select
                            className='form-control'
                            value={manualPaymentForm.type}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, type: e.target.value })}
                            required
                          >
                            <option value='IN'>Вхідний</option>
                            <option value='OUT'>Вихідний</option>
                          </select>
                        </div>
                        <div className='form-group'>
                          <label className='font-weight-bold'>Коментар адміна</label>
                          <textarea
                            className='form-control'
                            rows='3'
                            value={manualPaymentForm.adminComment}
                            onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, adminComment: e.target.value })}
                            placeholder='Введіть коментар (наприклад, від кого оплата)'
                          />
                        </div>
                      </div>
                      <div className='modal-footer'>
                        <button 
                          type='button' 
                          className='btn btn-secondary' 
                          onClick={() => { setShowManualPaymentModal(false); setManualPaymentError(''); }}
                        >
                          Скасувати
                        </button>
                        <button 
                          type='button' 
                          className='btn btn-success' 
                          onClick={handleCreateManualPayment}
                        >
                          Створити оплату
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Модальне вікно для редагування ручної оплати */}
              {showEditManualPaymentModal && paymentToEdit && (
                <div 
                  className='modal fade show' 
                  style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                  tabIndex='-1' 
                  role='dialog'
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowEditManualPaymentModal(false);
                      setPaymentToEdit(null);
                      setEditManualPaymentError('');
                    }
                  }}
                >
                  <div className='modal-dialog modal-dialog-centered' role='document' onClick={(e) => e.stopPropagation()}>
                    <div className='modal-content'>
                      <div className='modal-header'>
                        <h5 className='modal-title font-weight-bold'>Редагувати ручну оплату #{paymentToEdit.id}</h5>
                        <button 
                          type='button' 
                          className='close' 
                          onClick={() => { setShowEditManualPaymentModal(false); setPaymentToEdit(null); setEditManualPaymentError(''); }}
                          aria-label='Close'
                        >
                          <span aria-hidden='true'>&times;</span>
                        </button>
                      </div>
                      <div className='modal-body'>
                        {editManualPaymentError && (
                          <div className='alert alert-danger' role='alert'>
                            {editManualPaymentError}
                          </div>
                        )}
                        <div className='form-group'>
                          <label className='font-weight-bold'>Сума *</label>
                          <input
                            type='number'
                            className='form-control'
                            step='0.01'
                            min='0.01'
                            value={editManualPaymentForm.amount}
                            onChange={(e) => setEditManualPaymentForm({ ...editManualPaymentForm, amount: e.target.value })}
                            placeholder='Введіть суму'
                            required
                          />
                        </div>
                        <div className='form-group'>
                          <label className='font-weight-bold'>Тип *</label>
                          <select
                            className='form-control'
                            value={editManualPaymentForm.type}
                            onChange={(e) => setEditManualPaymentForm({ ...editManualPaymentForm, type: e.target.value })}
                            required
                          >
                            <option value='IN'>Вхідний</option>
                            <option value='OUT'>Вихідний</option>
                          </select>
                        </div>
                        <div className='form-group'>
                          <label className='font-weight-bold'>Коментар адміна</label>
                          <textarea
                            className='form-control'
                            rows='3'
                            value={editManualPaymentForm.adminComment}
                            onChange={(e) => setEditManualPaymentForm({ ...editManualPaymentForm, adminComment: e.target.value })}
                            placeholder='Введіть коментар (наприклад, від кого оплата)'
                          />
                        </div>
                      </div>
                      <div className='modal-footer'>
                        <button 
                          type='button' 
                          className='btn btn-secondary' 
                          onClick={() => { setShowEditManualPaymentModal(false); setPaymentToEdit(null); setEditManualPaymentError(''); }}
                        >
                          Скасувати
                        </button>
                        <button 
                          type='button' 
                          className='btn btn-success' 
                          onClick={handleUpdateManualPayment}
                        >
                          Зберегти
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pageCount > 1 && (
                <div className='d-flex justify-content-center mt-4'>
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    breakClassName={'break-me'}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageClick}
                    containerClassName={'pagination'}
                    subContainerClassName={'pages pagination'}
                    activeClassName={'active'}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Модальне вікно для відображення повного коментаря */}
      {showFullCommentModal && (
        <div 
          className='modal fade show' 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex='-1' 
          role='dialog'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFullCommentModal(false);
            }
          }}
        >
          <div className='modal-dialog modal-dialog-centered' role='document' onClick={(e) => e.stopPropagation()}>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title font-weight-bold'>Коментар адміна</h5>
                <button 
                  type='button' 
                  className='close' 
                  onClick={() => setShowFullCommentModal(false)}
                  aria-label='Close'
                >
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>{fullCommentText}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='row'>
        <div className='col p-0'>
          <Footer />
        </div>
      </div>
    </div>
      </div>
    </>
  );
};

export default AdminPaymentsPage;
